<template>
  <div :class="{'v2-card': !embedded}" data-qid="engagement-schedule-card">
    <div class="v2-card__head" v-if="!embedded">
      <h3>{{ $t('projects.tab.schedule') }}</h3>
      <span class="v2-tag" :class="project.overdue_notifications ? 'v2-tag--teal' : 'v2-tag--grey'">
        <font-awesome-icon :icon="project.overdue_notifications ? 'bell' : 'bell-slash'" class="mr-1"/>{{ project.overdue_notifications ? $t('projects.engagement.overview.reminders_on') : $t('projects.engagement.overview.reminders_off') }}
      </span>
    </div>

    <!-- Timeline from start to due date with today -->
    <div class="v2-timeline" v-if="span > 0">
      <div class="v2-timeline__track"></div>
      <div class="v2-timeline__fill" :style="{ width: position(today) + '%' }"></div>
      <div class="v2-timeline__today" :style="{ left: position(today) + '%' }">
        <span>{{ $t('projects.schedule.today') }}</span>
      </div>
      <div v-for="date in dates"
           :key="'mark-' + date.key"
           class="v2-timeline__mark"
           :class="'v2-timeline__mark--' + date.key"
           :style="{ left: 'calc(' + position(date.value) + '% - 8px)' }"
      ></div>
    </div>

    <div class="v2-dates">
      <span v-if="!dates.length" class="text-muted" data-qid="schedule-no-dates">{{ $t('system.missing_input') }}</span>
      <div v-for="date in dates" :key="'date-' + date.key" class="v2-date">
        <span class="v2-date__icon" :class="'v2-date__icon--' + date.key"><font-awesome-icon :icon="date.icon"/></span>
        <div class="v2-date__text">
          <div class="v2-date__label">{{ $t(date.label) }}</div>
          <div class="v2-date__value">{{ date.value | formatDate('DD MMM YYYY', true) }}</div>
        </div>
        <span class="v2-date__relative" :class="{'v2-date__relative--soon': date.due && days(date.value) >= 0 && days(date.value) <= 7, 'v2-date__relative--late': date.due && days(date.value) < 0 && isOpen}">{{ relative(date.value) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Compact schedule for the Overview and Planning: start, documentation collection due and project due
export default {
  name: 'EngagementScheduleCard',
  props: {
    project: {
      type: Object,
      required: true
    },
    // Inside another card (Planning), without its own header
    embedded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dates() {
      let dates = [];
      if (this.project.start_date) {
        dates.push({ key: 'start', value: this.project.start_date, label: 'projects.schedule.start_date_label', icon: 'play' });
      }
      // Only audits collect documentation; other projects can have the date from before (US 9480)
      if (this.project.data_collection_end_date && this.project.type === 'control_assessment') {
        dates.push({ key: 'documentation', value: this.project.data_collection_end_date, label: 'projects.schedule.data_due_date_label', icon: 'folder-open', due: true });
      }
      if (this.project.end_date) {
        dates.push({ key: 'end', value: this.project.end_date, label: 'projects.schedule.due_date_label', icon: 'calendar-alt', due: true });
      }
      return dates;
    },
    today() {
      const moment = require('moment');
      return moment().format('YYYY-MM-DD');
    },
    start() {
      return this.dateValue(this.project.start_date);
    },
    span() {
      return this.project.start_date && this.project.end_date ? this.dateValue(this.project.end_date) - this.start : 0;
    },
    isOpen() {
      return this.project.status !== 'completed' && this.project.status !== 'closed';
    }
  },
  methods: {
    dateValue(value) {
      const moment = require('moment');
      return moment(String(value).substring(0, 10), 'YYYY-MM-DD').valueOf();
    },
    position(value) {
      return Math.max(0, Math.min(100, (this.dateValue(value) - this.start) / this.span * 100));
    },
    days(value) {
      const moment = require('moment');
      return moment(String(value).substring(0, 10), 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
    },
    relative(value) {
      const days = this.days(value);
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      return days > 0 ? this.$tc('projects.schedule.in_days', days, { count: days }) : this.$tc('projects.schedule.days_ago', -days, { count: -days });
    }
  }
}
</script>
